import { useCallback, useEffect } from 'react';
import { isAfter } from 'date-fns';
import {
  SECONDS_TO_MILLISECONDS,
  DAYS_TO_HOURS,
  TWO_DAYS,
  ONE_HOUR,
  useTimer,
  TimeLeft
} from '@moda/portal-stanchions';

export interface Countdown {
  timeLeft: TimeLeft;
  message: string;
}

export type UseCountdown = [TimeLeft, () => string];

const formatDuration = (duration: number | string, units: string) =>
  `${duration} ${units}${Number(duration) > 1 ? 'S' : ''}`;

const humanizeTimeLeft = ({
  yearsLeft,
  monthsLeft,
  daysLeft,
  hoursLeft,
  minutesLeft
}: TimeLeft) => {
  if (parseInt(yearsLeft, 10) > 0) {
    return formatDuration(yearsLeft, 'YEAR');
  } else if (parseInt(monthsLeft, 10) > 0) {
    return formatDuration(monthsLeft, 'MONTH');
  } else if (parseInt(daysLeft, 10) > TWO_DAYS) {
    return formatDuration(daysLeft, 'DAY');
  } else if (parseInt(daysLeft, 10) >= 1 || parseInt(hoursLeft, 10) >= ONE_HOUR) {
    const days = parseInt(daysLeft, 10);
    const hours = days * DAYS_TO_HOURS + parseInt(hoursLeft, 10);
    return formatDuration(hours, 'HOUR');
  } else {
    return formatDuration(minutesLeft, 'MINUTE');
  }
};

export const useCountdown = (
  trunkshowActiveStartI: number,
  trunkshowActiveEndI: number
): UseCountdown => {
  const [timeLeft, setEndDate] = useTimer(
    isAfter(trunkshowActiveStartI * SECONDS_TO_MILLISECONDS, new Date())
      ? trunkshowActiveStartI * SECONDS_TO_MILLISECONDS
      : trunkshowActiveEndI * SECONDS_TO_MILLISECONDS
  );

  useEffect(() => {
    if (!isAfter(trunkshowActiveStartI * SECONDS_TO_MILLISECONDS, new Date())) {
      setEndDate(trunkshowActiveEndI * SECONDS_TO_MILLISECONDS);
    }
  }, [setEndDate, timeLeft.isOver, trunkshowActiveEndI, trunkshowActiveStartI]);

  const getMessageToDisplay = useCallback(() => {
    if (isAfter(trunkshowActiveStartI * SECONDS_TO_MILLISECONDS, new Date())) {
      return `LIVE IN ${humanizeTimeLeft(timeLeft)}`;
    } else if (isAfter(trunkshowActiveEndI * SECONDS_TO_MILLISECONDS, new Date())) {
      return `TRUNKSHOW ENDING IN ${humanizeTimeLeft(timeLeft)}`;
    } else {
      return 'ENDED';
    }
  }, [trunkshowActiveStartI, trunkshowActiveEndI, timeLeft]);

  return [timeLeft, getMessageToDisplay];
};
